// MIT License

// Copyright (c) 2017 Jared Palmer http://jaredpalmer.com

// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:

// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.

// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

import React, { useReducer, useRef, useCallback, useEffect, useMemo } from 'react';
import isEqual from 'react-fast-compare';
import invariant from 'tiny-warning';
import { unstable_runWithPriority, unstable_LowPriority } from 'scheduler';
import useEventCallback from '../../../../common/hooks/useEventCallback';
import { NavigationDefinition } from '../configuration/functional/FormNavigationConfiguration';
import { FunctionalValidationCondition } from '../configuration/functional/FormValidationConfiguration';
import { NaVaFormConfig, NaVaFormErrors, NaVaFormState, NaVaFormTouched, NaVaFormValues, FieldValidator, NaVaFormContextType, Computation, NewFieldValidator, ComputationOptions, ComputationCondition } from './types';
import { isFunction, setIn, setNestedObjectValues, getActiveElement, getIn } from './commonUtils';
import { getValueForCheckbox, getSelectedValues } from './formUtils';
import { NaVaFormContext } from './NaVaFormContext';
import { DistinctQueue, Queue } from './Queue';
import PromiseQueue from './PromiseQueue';
import { log, warn } from '../../../../util/LoggingUtils';

interface FieldRegistryEntry {
  ref: HTMLElement;
  keyDownHandler: (event: KeyboardEvent) => void;
}

// This is an object that contains a map of all registered fields
interface FieldRegistry {
  [field: string]: FieldRegistryEntry;
}

interface ComputationEntry {
  dependency: string;
  target: string;
  computation: Computation;
  condition?: ComputationCondition;
}

type NaVaFormMessage<TValues> =
  | { type: 'SUBMIT_ATTEMPT' }
  | { type: 'SUBMIT_FAILURE' }
  | { type: 'SUBMIT_SUCCESS' }
  | { type: 'SET_ISVALIDATING'; payload: boolean }
  | { type: 'SET_ISSUBMITTING'; payload: boolean }
  | { type: 'SET_VALUES'; payload: TValues }
  | { type: 'SET_FIELD_VALUE'; payload: { field: string; value?: any } }
  | { type: 'SET_FIELD_TOUCHED'; payload: { field: string; value?: boolean } }
  | { type: 'SET_FIELD_ERROR'; payload: { field: string; value?: string } }
  | { type: 'SET_MANUAL_FIELD_ERROR'; payload: { field: string; value?: string } }
  | { type: 'SET_TOUCHED'; payload: NaVaFormTouched<TValues> }
  | { type: 'SET_ERRORS'; payload: NaVaFormErrors<TValues> }
  | { type: 'SET_STATUS'; payload: any }
  | {
    type: 'SET_FORMIK_STATE';
    payload: (s: NaVaFormState<TValues>) => NaVaFormState<TValues>;
  }
  | {
    type: 'RESET_FORM';
    payload: NaVaFormState<TValues>;
  };

// State reducer
function naVaFormReducer<TValues>(
  state: NaVaFormState<TValues>,
  msg: NaVaFormMessage<TValues>
) {
  switch (msg.type) {
    case 'SET_VALUES':
      return { ...state, values: msg.payload };
    case 'SET_TOUCHED':
      return { ...state, touched: msg.payload };
    case 'SET_ERRORS':
      if (isEqual(state.errors, msg.payload)) {
        return state;
      }

      return { ...state, errors: msg.payload };
    case 'SET_STATUS':
      return { ...state, status: msg.payload };
    case 'SET_ISSUBMITTING':
      return { ...state, isSubmitting: msg.payload };
    case 'SET_ISVALIDATING':
      return { ...state, isValidating: msg.payload };
    case 'SET_FIELD_VALUE':
      return {
        ...state,
        values: setIn(state.values, msg.payload.field, msg.payload.value)
      };
    case 'SET_FIELD_TOUCHED':
      return {
        ...state,
        touched: setIn(state.touched, msg.payload.field, msg.payload.value)
      };
    case 'SET_FIELD_ERROR':
      return {
        ...state,
        errors: setIn(state.errors, msg.payload.field, msg.payload.value)
      };
    case 'RESET_FORM':
      return { ...state, ...msg.payload };
    case 'SET_FORMIK_STATE':
      return msg.payload(state);
    case 'SUBMIT_ATTEMPT':
      return {
        ...state,
        touched: setNestedObjectValues<NaVaFormTouched<TValues>>(
          state.values,
          true
        ),
        isSubmitting: true,
        submitCount: state.submitCount + 1
      };
    case 'SUBMIT_FAILURE':
      return {
        ...state,
        isSubmitting: false
      };
    case 'SUBMIT_SUCCESS':
      return {
        ...state,
        isSubmitting: false
      };
    default:
      return state;
  }
}

// Initial empty states // objects
const emptyErrors: NaVaFormErrors<unknown> = {};
const emptyTouched: NaVaFormTouched<unknown> = {};

export function useNaVaForm<TValues extends NaVaFormValues = NaVaFormValues>({
  validateOnMount = false,
  enableReinitialize = false,
  navigationConfig = {},
  validationConfig = {},
  onSubmit,
  ...rest
}: NaVaFormConfig<TValues>): NaVaFormContextType<TValues> {
  const props = {
    validateOnMount,
    onSubmit,
    ...rest
  };
  const initialValues = useRef(props.initialValues);
  const initialErrors = useRef(props.initialErrors || emptyErrors);
  const initialTouched = useRef(props.initialTouched || emptyTouched);
  const initialStatus = useRef(props.initialStatus);
  const isMounted = useRef<boolean>(false);
  const fieldRegistry = useRef<FieldRegistry>({});
  const configuredValidators = useRef<Record<string, FieldValidator<TValues>>>({});
  const computations = useRef<ComputationEntry[]>([]);
  const validators = useRef<Record<string, NewFieldValidator[]>>({});

  useEffect(() => {
    configuredValidators.current = Object.keys(validationConfig).reduce<Record<string, FieldValidator<TValues>>>((prev, key) => {
      prev[key] = (values: TValues) => executeFieldValidation(validationConfig[key] || [], values);
      return prev;
    }, {});
  }, [validationConfig]);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  const [state, dispatch] = useReducer<
    React.Reducer<NaVaFormState<TValues>, NaVaFormMessage<TValues>>
  >(naVaFormReducer, {
    values: props.initialValues,
    errors: props.initialErrors || emptyErrors,
    touched: props.initialTouched || emptyTouched,
    status: props.initialStatus,
    isSubmitting: false,
    isValidating: false,
    submitCount: 0
  });

  const isFirstRunRef = useRef<boolean>(true);
  const prevComputedValues = useRef<TValues>(props.initialValues);
  const prevValidatedValues = useRef<TValues>(props.initialValues); // store a reference to previously validated values
  const previousErrors = useRef<NaVaFormErrors<TValues>>(props.initialErrors || emptyErrors);

  const tryNavigateBack = useEventCallback((fromField: string) => {
    if (!props.navigationRoot) return false;

    const visitedFields: Set<string> = new Set([props.navigationRoot]);
    let navConfig = navigationConfig[props.navigationRoot];
    let currField = determineFieldName(navConfig.next, state.values);
    let prevField: string | undefined;
    while (currField && currField !== fromField && !visitedFields.has(currField)) {
      visitedFields.add(currField);
      prevField = currField;
      navConfig = navigationConfig[currField];
      if (!navConfig) return false;
      currField = determineFieldName(navConfig.next, state.values);
    }
    if (currField !== fromField || !prevField) return false;
    if (isFieldDisabled(prevField)) tryNavigateBack(prevField);
    focusField(prevField);
    return true;
  });

  const tryNavigate = useEventCallback((fromField: string) => {
    const navConfig = navigationConfig[fromField];
    if (!navConfig || !navConfig.next) return false;
    const nextFieldName = determineFieldName(navConfig.next, state.values);
    if (!nextFieldName) return false;
    if (isFieldDisabled(nextFieldName)) tryNavigate(nextFieldName);
    focusField(nextFieldName);
    return true;
  });

  const handleKeyDown = useEventCallback((name: string, e: KeyboardEvent) => {
    const navConfig = navigationConfig[name];
    if (!navConfig) return;
    switch (e.key) {
      case 'Tab':
        if (e.getModifierState('Shift')) {
          if (tryNavigateBack(name)) e.preventDefault();
        } else if (tryNavigate(name)) e.preventDefault();
        break;
      // case 'Enter':
      //   if (tryNavigate(navConfig.fastNext)) e.preventDefault();
      //   break;
      default:
        break;
    }
  });

  const registerField = useCallback(
    (name: string, ref: HTMLElement) => {
      const keyDownHandler = (event: KeyboardEvent) => {
        handleKeyDown(name, event);
      };
      ref.addEventListener('keydown', keyDownHandler);
      const entry: FieldRegistryEntry = { ref, keyDownHandler };
      fieldRegistry.current[name] = entry;
    },
    [handleKeyDown]
  );

  const unregisterField = useCallback((name: string) => {
    if (!fieldRegistry.current[name]) return;
    const { ref, keyDownHandler: listener } = fieldRegistry.current[name];
    if (ref) {
      ref.removeEventListener('keydown', listener);
    }
    delete fieldRegistry.current[name];
  }, []);

  const getFieldRef = useCallback((name: string) => (element: HTMLElement | null): void => {
    if (element !== null) registerField(name, element);
    else unregisterField(name);
  }, [registerField, unregisterField]);

  const registerValidator = useCallback((path: string | string[], validator: NewFieldValidator) => {
    const name = typeof path === 'string' ? path : path.join('.');
    if (!validators.current.hasOwnProperty(name)) {
      validators.current[name] = [];
    }
    validators.current[name].push(validator);
  }, []);

  const unregisterValidator = useCallback((path: string | string[], validator: NewFieldValidator) => {
    const name = typeof path === 'string' ? path : path.join('.');
    if (!validators.current.hasOwnProperty(name)) return;
    const idx = validators.current[name].indexOf(validator);
    if (idx > -1) {
      validators.current[name].splice(idx, 1);
    }
    if (validators.current[name].length > 0) return;
    delete validators.current[name];
  }, []);

  const registerComputation = useCallback((opts: ComputationOptions) => {
    const dependency = typeof opts.dependency === 'string' ? opts.dependency : opts.dependency.join('.');
    const target = typeof opts.target === 'string' ? opts.target : opts.target.join('.');
    computations.current.push({
      dependency,
      target,
      computation: opts.computation,
      condition: opts.condition
    });
  }, []);

  const unregisterComputation = useCallback((opts: ComputationOptions) => {
    const dependency = typeof opts.dependency === 'string' ? opts.dependency : opts.dependency.join('.');
    const target = typeof opts.target === 'string' ? opts.target : opts.target.join('.');
    const idx = computations.current.findIndex(ce =>
      ce.dependency === dependency &&
      ce.target === target &&
      ce.computation === opts.computation &&
      ce.condition === opts.condition
    );
    if (idx < 0) return;
    computations.current.splice(idx, 1);
  }, []);

  const isFieldDisabled = useCallback((fieldName: string) => {
    const registryEntry = fieldRegistry.current[fieldName];
    if (!registryEntry) {
      console.warn(`Tried to focus field '${fieldName}' which is not a part of the current form. Check your navigation configuration`);
      return true;
    }
    if (registryEntry.ref instanceof HTMLInputElement) {
      return registryEntry.ref.disabled;
    }
    return true;
  }, []);

  const focusField = useCallback((fieldName: string) => {
    const registryEntry = fieldRegistry.current[fieldName];
    if (!registryEntry) {
      warn(`Tried to focus field '${fieldName}' which is not a part of the current form. Check your navigation configuration`);
      return;
    }
    registryEntry.ref.focus();
  }, []);

  // Checks if the values of the passed fields have changed since the last run and adds dependent computations to the queue
  // changed values will be merged with the previous values and the result is returned to allow updating them
  const enqueueChangedFields = useCallback((computations: ComputationEntry[], queue: Queue<ComputationEntry>, values: TValues, prevValues: TValues, fields: string[], triggeredBy?: string) => {
    const distinctFields = [...new Set(fields)];
    const changedTuples = distinctFields
      .map<[string, any, any]>(field => [field, getIn(prevValues, field), getIn(values, field)]) // build tuples [<fieldName>, <previous value>, <current value>]
      .filter(([, prevValue, currValue]) => prevValue !== currValue); // only keep the tuples where previous and current value differs

    const newComputations = computations
      .filter(computation =>
        changedTuples.some(([changedField, cfprevValm, cfcurrVal]) => {
          let sameField = false;
          if (computation.dependency === changedField) sameField = true;
          if (sameField && computation.dependency === computation.target && computation.dependency === triggeredBy) {
            console.warn(`Prevented possible infinite loop by not running a computation that triggered itself (${computation.target}).`);
            return false;
          }
          let childField = false;
          if (!sameField && isChildField(changedField, computation.dependency)) {
            const prevValue = getIn(prevValues, computation.dependency);
            const currValue = getIn(values, computation.dependency);
            childField = prevValue !== currValue;
            if (childField) log(`enqueuing ${computation.dependency} because ${changedField} changed:`, prevValue, currValue, computation);
          }
          if (sameField) log(`enqueuing ${computation.dependency} because ${changedField} changed:`, cfprevValm, cfcurrVal, computation);
          return sameField || childField;
        })
      );
    queue.enqueue(...newComputations);
    return changedTuples.reduce((prev, [field, , currValue]) => setIn(prev, field, currValue), prevValues); // cache the current values as previous values for the next run
  }, []);

  // Runs all necessary computations, where necessary means that ALL computations will be done on the first run and only computations for changed values in subsequent runs
  // returns the newly computed values
  const runComputations = useCallback(async (values: TValues, isFirstRun?: boolean) => {
    const computationQueue = DistinctQueue<ComputationEntry>();
    const depFields = computations.current.map(c => c.dependency);

    if (isFirstRun) computationQueue.enqueue(...computations.current);
    else prevComputedValues.current = enqueueChangedFields(computations.current, computationQueue, values, prevComputedValues.current, depFields);

    const getValue = (path: string | string[]) => getIn(values, path);

    while (!computationQueue.isEmpty) {
      const entry = computationQueue.dequeue();
      const depValue = getValue(entry.dependency);
      if (entry.condition && !entry.condition(depValue, getValue)) continue;
      const computedValue = await Promise.resolve(entry.computation(depValue, getValue));
      values = setIn(values, entry.target, computedValue);
      prevComputedValues.current = enqueueChangedFields(computations.current, computationQueue, values, prevComputedValues.current, [entry.target], entry.dependency);
    }
    return values;
  }, [enqueueChangedFields]);

  const runFieldValidations = useCallback(
    async (field: string, values: TValues, isFirstRun?: boolean): Promise<string | string[] | undefined> => {
      const fieldValue = getIn(values, field);
      // if (!isFirstRun) {
      //   const prevFieldValue = getIn(prevValidatedValues.current, field);
      //   if (fieldValue === prevFieldValue) {
      //     return getIn(previousErrors.current, field);
      //   }
      // }
      prevValidatedValues.current = setIn(prevValidatedValues.current, field, fieldValue);

      const validationErrors: string[] = [];
      const configValidator = configuredValidators.current[field];
      if (configValidator) {
        const error = await Promise.resolve(configValidator(values));
        if (error) validationErrors.push(error);
      }
      if (validators.current.hasOwnProperty(field)) {
        const fieldValidators = validators.current[field];
        const getValue = (path: string | string[]) => getIn(values, path);
        const errors = await Promise.all(fieldValidators.map(fv => fv(getValue(field), getValue)));
        errors.filter(e => e).forEach(e => validationErrors.push(e as string));
      }
      const validationResult = validationErrors.length === 0
        ? undefined
        : validationErrors.length === 1
          ? validationErrors[0]
          : validationErrors;

      previousErrors.current = setIn(previousErrors.current, field, validationResult);
      return validationResult;
    },
    []
  );

  const runValidations = useCallback(
    async (values: TValues, isFirstRun?: boolean): Promise<NaVaFormErrors<TValues>> => {
      const oldValues = values;
      values = await runComputations(values, isFirstRun);
      if (oldValues !== values) dispatch({ type: 'SET_VALUES', payload: values });

      const fieldKeysWithValidation: string[] = Object.keys(configuredValidators.current);
      Object.keys(validators.current).forEach(f => {
        if (validators.current[f] && validators.current[f].length > 0 && !fieldKeysWithValidation.includes(f)) {
          fieldKeysWithValidation.push(f);
        }
      });

      // Construct an array with all of the field validation functions
      const fieldValidations: Promise<string | string[] | undefined>[] =
        fieldKeysWithValidation.length > 0
          ? fieldKeysWithValidation.map(f =>
            runFieldValidations(f, values, isFirstRun)
          )
          : [Promise.resolve('DO_NOT_DELETE_YOU_WILL_BE_FIRED')]; // use special case ;)
      const fieldErrorsList = await Promise.all(fieldValidations);
      return fieldErrorsList.reduce<NaVaFormErrors<TValues>>((prev, curr, index) => {
        if (curr === 'DO_NOT_DELETE_YOU_WILL_BE_FIRED') {
          return prev;
        }
        if (curr) {
          prev = setIn(prev, fieldKeysWithValidation[index], curr);
        }
        return prev;
      }, {});
    },
    [runFieldValidations, runComputations]
  );

  const validationQueueRef = useRef(PromiseQueue<NaVaFormErrors<TValues>>());

  // The only safe way to run computations and validations is by queueing them, because parallel processing could lead to
  // unpredictable changes to/overwrites of the previous values
  const queueValidations = useCallback(
    (values: TValues) => {
      const isFirstRun = isFirstRunRef.current;
      isFirstRunRef.current = false;
      return validationQueueRef.current.enqueue(() => runValidations(values, isFirstRun));
    },
    [runValidations]
  );

  // Run validations and dispatching the result as low-priority via rAF.
  //
  // The thinking is that validation as a result of onChange and onBlur
  // should never block user input. Note: This method should never be called
  // during the submission phase because validation prior to submission
  // is actaully high-priority since we absolutely need to guarantee the
  // form is valid before executing props.onSubmit.
  const validateFormWithLowPriority = useEventCallback(
    (values: TValues = state.values) => unstable_runWithPriority(unstable_LowPriority, async () => {
      try {
        const combinedErrors = await queueValidations(values);
        if (!!isMounted.current) {
          dispatch({ type: 'SET_ERRORS', payload: combinedErrors });
        }
        return combinedErrors;
      } catch (actualException) {
        if (process.env.NODE_ENV !== 'production') {
          // Users can throw during validate, however they have no way of handling their error on touch / blur. In low priority, we need to handle it
          console.warn(
            'Warning: An unhandled error was caught during low priority validation in <Formik validate />',
            actualException
          );
        }
      }
    })
  );

  // Run all validations methods and update state accordingly
  const validateFormWithHighPriority = useEventCallback(
    async (values: TValues = state.values) => {
      dispatch({ type: 'SET_ISVALIDATING', payload: true });
      const combinedErrors = await queueValidations(values);
      if (!!isMounted.current) {
        dispatch({ type: 'SET_ISVALIDATING', payload: false });
        if (!isEqual(state.errors, combinedErrors)) {
          dispatch({ type: 'SET_ERRORS', payload: combinedErrors });
        }
      }
      return combinedErrors;
    }
  );

  useEffect(() => {
    if (validateOnMount && isMounted.current === true) {
      void validateFormWithLowPriority(initialValues.current);
    }
  }, [validateOnMount, validateFormWithLowPriority]);

  const resetForm = useCallback(
    (nextState?: Partial<NaVaFormState<TValues>>) => {
      const values =
        nextState && nextState.values
          ? nextState.values
          : initialValues.current;
      const errors =
        nextState && nextState.errors
          ? nextState.errors
          : initialErrors.current
            ? initialErrors.current
            : props.initialErrors || {};
      const touched =
        nextState && nextState.touched
          ? nextState.touched
          : initialTouched.current
            ? initialTouched.current
            : props.initialTouched || {};
      const status =
        nextState && nextState.status
          ? nextState.status
          : initialStatus.current
            ? initialStatus.current
            : props.initialStatus;
      initialValues.current = values;
      initialErrors.current = errors;
      initialTouched.current = touched;
      initialStatus.current = status;

      prevComputedValues.current = values;
      prevValidatedValues.current = values;
      previousErrors.current = errors;
      isFirstRunRef.current = true;

      const dispatchFn = () => {
        dispatch({
          type: 'RESET_FORM',
          payload: {
            isSubmitting: !!nextState && !!nextState.isSubmitting,
            errors,
            touched,
            status,
            values,
            isValidating: !!nextState && !!nextState.isValidating,
            submitCount:
              !!nextState &&
                !!nextState.submitCount &&
                typeof nextState.submitCount === 'number'
                ? nextState.submitCount
                : 0
          }
        });
      };

      if (props.onReset) {
        Promise.resolve(
          props.onReset.call(
            undefined,
            state.values
          )
        )
          .then(dispatchFn)
          .then(() => validateFormWithLowPriority(values))
          .catch(() => { });
        // ToDo: Check if validation on reset works as expected
      } else {
        dispatchFn();
      }
    },
    [props.initialErrors, props.initialStatus, props.initialTouched, props.onReset, state.values, validateFormWithLowPriority]
  );

  useEffect(() => {
    if (
      enableReinitialize &&
      isMounted.current === true &&
      !isEqual(initialValues.current, props.initialValues)
    ) {
      initialValues.current = props.initialValues;
      resetForm();
    }
  }, [enableReinitialize, props.initialValues, resetForm]);

  useEffect(() => {
    if (
      enableReinitialize &&
      isMounted.current === true &&
      !isEqual(initialErrors.current, props.initialErrors)
    ) {
      initialErrors.current = props.initialErrors || emptyErrors;
      dispatch({
        type: 'SET_ERRORS',
        payload: props.initialErrors || emptyErrors
      });
    }
  }, [enableReinitialize, props.initialErrors]);

  useEffect(() => {
    if (
      enableReinitialize &&
      isMounted.current === true &&
      !isEqual(initialTouched.current, props.initialTouched)
    ) {
      initialTouched.current = props.initialTouched || emptyTouched;
      dispatch({
        type: 'SET_TOUCHED',
        payload: props.initialTouched || emptyTouched
      });
    }
  }, [enableReinitialize, props.initialTouched]);

  useEffect(() => {
    if (
      enableReinitialize &&
      isMounted.current === true &&
      !isEqual(initialStatus.current, props.initialStatus)
    ) {
      initialStatus.current = props.initialStatus;
      dispatch({
        type: 'SET_STATUS',
        payload: props.initialStatus
      });
    }
  }, [enableReinitialize, props.initialStatus, props.initialTouched]);

  const setTouched = useEventCallback((touched: NaVaFormTouched<TValues>, shouldValidate?: boolean) => {
    dispatch({ type: 'SET_TOUCHED', payload: touched });
    const willValidate = shouldValidate === undefined ? false : shouldValidate;
    return willValidate
      ? validateFormWithLowPriority(state.values)
      : Promise.resolve();
  });

  const setErrors = useCallback((errors: NaVaFormErrors<TValues>) => {
    dispatch({ type: 'SET_ERRORS', payload: errors });
  }, []);

  const setValues = useEventCallback((values: TValues, shouldValidate?: boolean) => {
    dispatch({ type: 'SET_VALUES', payload: values });
    const willValidate = shouldValidate === undefined ? false : shouldValidate;
    return willValidate
      ? validateFormWithLowPriority(values)
      : Promise.resolve();
  });

  const setFieldError = useCallback((field: string, value: string | undefined) => {
    dispatch({
      type: 'SET_MANUAL_FIELD_ERROR',
      payload: { field, value }
    });
  }, []);

  const setFieldValue = useEventCallback((field: string, value: any, shouldValidate?: boolean) => {
    dispatch({
      type: 'SET_FIELD_VALUE',
      payload: {
        field,
        value
      }
    });
    const willValidate = shouldValidate === undefined ? false : shouldValidate;
    return willValidate
      ? validateFormWithLowPriority(setIn(state.values, field, value))
      : Promise.resolve();
  });

  const handleChange = useEventCallback((event: React.ChangeEvent<any>) => {
    // Persist the event
    // @see https://reactjs.org/docs/events.html#event-pooling
    if (event.persist) {
      event.persist();
    }

    const target = event.target
      ? (event).target
      : (event).currentTarget;

    const { type, name, id, value, checked, outerHTML, options, multiple } = target;

    const field = name ? name : id;
    if (!field && process.env.NODE_ENV === 'development') {
      warnAboutMissingIdentifier({
        htmlContent: outerHTML,
        documentationAnchorLink: 'handlechange-e-reactchangeeventany--void',
        handlerName: 'handleChange'
      });
    }

    let parsed;
    const val = /number|range/.test(type)
      ? ((parsed = parseFloat(value)), isNaN(parsed) ? null : parsed)
      : type.includes('checkbox') // checkboxes
        ? getValueForCheckbox(getIn(state.values, field), checked, value)
        : !!multiple // <select multiple>
          ? getSelectedValues(options)
          : value;

    if (field) {
      // Set form fields by name
      void setFieldValue(field, val);
    }
  });

  const setFieldTouched = useEventCallback((field: string, touched = true, shouldValidate?: boolean) => {
    dispatch({
      type: 'SET_FIELD_TOUCHED',
      payload: {
        field,
        value: touched
      }
    });
    const willValidate = shouldValidate === undefined ? true : shouldValidate;
    return willValidate
      ? validateFormWithLowPriority(state.values)
      : Promise.resolve();
  });

  const handleBlur = useEventCallback((e: React.FocusEvent<any>) => {
    e.persist();
    const { name, id, outerHTML } = e.target;
    const field = name ? name : id;

    if (!field && process.env.NODE_ENV === 'production') {
      warnAboutMissingIdentifier({
        htmlContent: outerHTML,
        documentationAnchorLink: 'handleblur-e-any--void',
        handlerName: 'handleBlur'
      });
    }

    void setFieldTouched(field, true);
  });

  const setFormikState = useCallback((stateOrCb: NaVaFormState<TValues> | ((state: NaVaFormState<TValues>) => NaVaFormState<TValues>)): void => {
    if (isFunction(stateOrCb)) {
      dispatch({ type: 'SET_FORMIK_STATE', payload: stateOrCb });
    } else {
      dispatch({ type: 'SET_FORMIK_STATE', payload: () => stateOrCb });
    }
  }, []);

  const setStatus = useCallback((status: any) => {
    dispatch({ type: 'SET_STATUS', payload: status });
  }, []);

  const setSubmitting = useCallback((isSubmitting: boolean) => {
    dispatch({ type: 'SET_ISSUBMITTING', payload: isSubmitting });
  }, []);

  const submitForm = useEventCallback(async () => {
    dispatch({ type: 'SUBMIT_ATTEMPT' });
    const combinedErrors = await validateFormWithHighPriority();
    // In case an error was thrown and passed to the resolved Promise,
    // `combinedErrors` can be an instance of an Error. We need to check
    // that and abort the submit.
    // If we don't do that, calling `Object.keys(new Error())` yields an
    // empty array, which causes the validation to pass and the form
    // to be submitted.

    const isInstanceOfError = combinedErrors instanceof Error;
    const isActuallyValid =
      !isInstanceOfError && Object.keys(combinedErrors).length === 0;
    if (isActuallyValid) {
      // Proceed with submit...
      //
      // To respect sync submit fns, we can't simply wrap executeSubmit in a promise and
      // _always_ dispatch SUBMIT_SUCCESS because isSubmitting would then always be false.
      // This would be fine in simple cases, but make it impossible to disable submit
      // buttons where people use callbacks or promises as side effects (which is basically
      // all of v1 Formik code). Instead, recall that we are inside of a promise chain already,
      //  so we can try/catch executeSubmit(), if it returns undefined, then just bail.
      // If there are errors, throw em. Otherwise, wrap executeSubmit in a promise and handle
      // cleanup of isSubmitting on behalf of the consumer.
      let promiseOrUndefined;
      try {
        promiseOrUndefined = executeSubmit();
        // Bail if it's sync, consumer is responsible for cleaning up
        // via setSubmitting(false)
        if (promiseOrUndefined === undefined) {
          return;
        }
      } catch (error) {
        throw error;
      }
      try {
        await Promise.resolve(promiseOrUndefined);
        if (!!isMounted.current) {
          dispatch({ type: 'SUBMIT_SUCCESS' });
        }
      } catch (_errors) {
        if (!!isMounted.current) {
          dispatch({ type: 'SUBMIT_FAILURE' });
          // This is a legit error rejected by the onSubmit fn
          // so we don't want to break the promise chain
          throw _errors;
        }
      }
    } else if (!!isMounted.current) {
      // ^^^ Make sure Formik is still mounted before updating state
      dispatch({ type: 'SUBMIT_FAILURE' });
      // throw combinedErrors;
      if (isInstanceOfError) {
        throw combinedErrors;
      }
    }
    return;
  });

  const handleSubmit = useEventCallback(
    async (e?: React.FormEvent<HTMLFormElement>) => {
      if (e) {
        if (e.preventDefault && isFunction(e.preventDefault)) e.preventDefault();
        if (e.stopPropagation && isFunction(e.stopPropagation)) e.stopPropagation();
      }

      // Warn if form submission is triggered by a <button> without a
      // specified `type` attribute during development. This mitigates
      // a common gotcha in forms with both reset and submit buttons,
      // where the dev forgets to add type="button" to the reset button.
      if (process.env.NODE_ENV === 'production' && typeof document !== 'undefined') {
        // Safely get the active element (works with IE)
        const activeElement = getActiveElement();
        if (
          activeElement !== null &&
          activeElement instanceof HTMLButtonElement
        ) {
          invariant(
            activeElement.attributes &&
            activeElement.attributes.getNamedItem('type'),
            'You submitted a Formik form using a button with an unspecified `type` attribute.  Most browsers default button elements to `type = "submit"`. If this is not a submit button, please add `type = "button"`.'
          );
        }
      }
      try {
        await submitForm();
      } catch (reason) {
        console.warn(
          'Warning: An unhandled error was caught from submitForm()',
          reason
        );
      }
    }
  );

  // const imperativeMethods: FormikHelpers<Values> = {
  //   resetForm,

  //   validateForm: validateFormWithHighPriority,
  //   validateField,
  //   setErrors,
  //   setFieldError,
  //   setFieldTouched,
  //   setFieldValue,
  //   setStatus,
  //   setSubmitting,
  //   setTouched,
  //   setValues,
  //   setFormikState,
  //   submitForm
  // };

  const executeSubmit = useEventCallback(() => onSubmit(state.values /* , imperativeMethods */));

  const handleReset = useEventCallback(e => {
    if (e) {
      if (e.preventDefault && isFunction(e.preventDefault)) e.preventDefault();
      if (e.stopPropagation && isFunction(e.stopPropagation)) e.stopPropagation();
    }
    resetForm();
  });

  const dirty = useMemo(
    () => !isEqual(initialValues.current, state.values),
    [state.values]
  );

  const isValid = useMemo(
    () => state.errors && Object.keys(state.errors).length === 0,
    [state.errors]
  );

  const ctx: NaVaFormContextType<TValues> = {
    ...state,
    initialValues: initialValues.current,
    initialErrors: initialErrors.current,
    initialTouched: initialTouched.current,
    initialStatus: initialStatus.current,
    handleBlur,
    handleChange,
    handleReset,
    handleSubmit,
    resetForm,
    setErrors,
    setFormikState,
    setFieldTouched,
    setFieldValue,
    setFieldError,
    setStatus,
    setSubmitting,
    setTouched,
    setValues,
    submitForm,
    validateFormWithLowPriority,
    validateForm: validateFormWithHighPriority,
    isValid,
    dirty,
    getFieldRef,
    focusField,
    registerComputation,
    unregisterComputation,
    registerValidator,
    unregisterValidator
  };

  return ctx;
}

function warnAboutMissingIdentifier({
  htmlContent,
  documentationAnchorLink,
  handlerName
}: {
  htmlContent: string;
  documentationAnchorLink: string;
  handlerName: string;
}) {
  console.warn(
    `Warning: Formik called \`${handlerName}\`, but you forgot to pass an \`id\` or \`name\` attribute to your input:
    ${htmlContent}
    Formik cannot determine which value to update. For more info see https://github.com/jaredpalmer/formik#${documentationAnchorLink}
  `
  );
}

function determineFieldName(navigationDefinition: NavigationDefinition[], fieldValues: Record<string, any>) {
  for (const field of navigationDefinition) {
    if (!field.validate) return field.fieldName;
    if (field.validate(fieldValues)) return field.fieldName;
  }
}

function executeFieldValidation(conditions: FunctionalValidationCondition[], values: NaVaFormValues) {
  if (!conditions) return;
  let message: string | undefined;
  for (const condition of conditions) {
    if (condition.validate(values)) return;
    else if (!message) message = condition.message;
  }
  return message;

}

function isChildField(parentField: string, childField: string) {
  const parentPath = parentField.split('.');
  const childPath = childField.split('.');
  if (parentPath.length >= childPath.length) return false;
  let isChildPath = true;
  let i = 0;
  while (i < parentPath.length && isChildPath) {
    if (parentPath[i] !== childPath[i]) isChildPath = false;
    i++;
  }
  return isChildPath;
}

export type NaVaFormProps = NaVaFormConfig<NaVaFormValues> & {
  className?: string;
  autoComplete?: boolean;
};

const NaVaForm: React.FC<React.PropsWithChildren<NaVaFormProps>> = ({ children, className, autoComplete = false, ...props }) => {

  const context = useNaVaForm(props);

  return (
    <NaVaFormContext.Provider value={context}>
      <form className={className} onSubmit={context.handleSubmit} onReset={context.handleReset} autoComplete={autoComplete ? undefined : 'off'}>
        {!autoComplete && <input autoComplete="off" name="hidden" type="text" style={{ display: 'none' }}></input>}
        {children}
      </form>
    </NaVaFormContext.Provider>
  );
};

export default NaVaForm;

